import { useQuery } from "@apollo/client";
import Errors from "components/base/Errors";
import { Select } from "components/base/Form";
import Spinner from "components/base/Spinner";
import PageV2 from "components/shared/PageV2";
import moment from "moment";
import { useContext } from "react";
import { BsArrowRight } from "react-icons/bs";
import { Link, useSearchParams } from "react-router-dom";
import { ObjectiveContext } from "./context";
import { FETCH_TEAMS_FOR_OBJECTIVES } from "./graphql";
import { computeProgress } from "./progress";

const STATUS_LIGHTS = {
  DELAYED: "🔴",
  AT_RISK: "🟡",
  ON_TRACK: "🔵",
  COMPLETED: "🟢",
};

const TeamsPage = () => {
  const { years } = useContext(ObjectiveContext);

  // Get current year as default value for the year filter, if not specified in the URL search params
  // If not found in the years list, default to "ALL"
  let defaultYear = new Date().getFullYear();
  if (!years.includes(defaultYear)) {
    defaultYear = "ALL";
  }

  const [searchParams, setSearchParams] = useSearchParams({
    year: defaultYear,
  });
  const params = Object.fromEntries(searchParams.entries());
  const year = params.year === "ALL" ? null : parseInt(params.year, 10);

  const { loading, error, data } = useQuery(FETCH_TEAMS_FOR_OBJECTIVES, {
    variables: { year },
    fetchPolicy: "network-only",
  });

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const teams = data.teams.map((team) => ({
    ...team,
    objectives: team.objectives.map((objective) => ({
      ...objective,
      progress: computeProgress(objective),
    })),
  }));

  const sortedObjectives = (objectives) => {
    const res = [...objectives].sort((a, b) =>
      moment(a.dueDate).diff(moment(b.dueDate)),
    );
    return res;
  };

  return (
    <PageV2
      title={
        <div className="flex items-center">
          <div>Team Objectives</div>
          <Select
            className="ml-2 text-base py-1 px-2 font-semibold text-blue-600 pl-2"
            value={year || "ALL"}
            onChange={(e) => {
              const value = e.target.value;
              setSearchParams({ ...params, year: value });
            }}
          >
            <option value="ALL">All Years</option>
            {years.map((i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </Select>
        </div>
      }
    >
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 text-sm">
        {teams.map((i) => (
          <div key={i.id} className="card relative group">
            <Link
              to={`${i.id}?year=${year || "ALL"}`}
              className="flex items-center space-x-2"
            >
              <h5>{i.name}</h5>
              <BsArrowRight
                className="opacity-0 group-hover:opacity-70 transition-all -translate-x-4 group-hover:translate-x-0"
                size={21}
              />
            </Link>

            <div className="mt-2">
              {i.objectives.length === 0 && (
                <div className="opacity-70">No objectives yet</div>
              )}

              <div className="-mx-2">
                <table>
                  <tbody>
                    {sortedObjectives(i.objectives).map((objective, index) => (
                      <tr
                        key={objective.id}
                        className="border-y border-gray-100 dark:border-gray-700"
                      >
                        <td>
                          <div className="flex items-center">
                            <span>
                              <span className="opacity-70">{index + 1}.</span>{" "}
                              {objective.description}{" "}
                              <span className="text-xs opacity-70">
                                {objective.dueDate}
                              </span>
                            </span>
                            <span className="pl-2 text-[9px] whitespace-nowrap">
                              {objective.keyResults
                                .map((i) => STATUS_LIGHTS[i.status])
                                .join(" ")}
                            </span>
                          </div>
                        </td>
                        <td className="text-right">
                          {objective.progress.toFixed(0)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))}
      </div>
    </PageV2>
  );
};

export default TeamsPage;
